import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";

import { observeStore } from "../../stores";
import { captureError } from "../../utils/captureError";
import * as styles from "./VideoStack.scss";

type VideoData = {
    duration: number;
    time: number;
    videoElement?: HTMLVideoElement;
    src: string;
};

class VideoStack extends HTMLElement {
    private name?: string;
    private activeVideo?: HTMLVideoElement;
    private ctx?: CanvasRenderingContext2D;
    private canvas?: HTMLCanvasElement;
    private videoDataList: VideoData[] = [];
    private videoTime?: number;
    private shouldPlayVideos = false;
    private isPlaying = false;
    private startedPreloading = false;
    private speed?: number;
    private timeSinceLastTimeSet = 0;
    private renderTimeoutHandle?: number;
    private lastTimelineTime?: number;
    private muted = true;
    private videoChangeTimeout?: NodeJS.Timeout;
    private aborted = false;

    constructor() {
        super();
    }

    private throttledAnimate = throttle(() => {
        this.startRenderLoop();
    }, duration.second / 30);

    buildCanvas() {
        if (!this.ctx) {
            // make the canvas if it doesn't exist
            this.canvas = document.createElement("canvas");
            this.canvas.width = 0;
            this.canvas.height = 0;
            this.ctx = this.canvas.getContext("2d") || undefined;
            if (this.ctx) {
                this.ctx.imageSmoothingEnabled = true;
            }
            this.appendChild(this.canvas);
        }
    }

    getVideo(data: VideoData, preload = false): HTMLVideoElement {
        if (!data.videoElement) {
            const existingVideoElement = this.videoDataList.find(
                v => v.src === data.src,
            )?.videoElement;

            if (existingVideoElement) {
                data.videoElement = existingVideoElement;
                if (preload && !existingVideoElement.src) {
                    existingVideoElement.src = data.src;
                    existingVideoElement.load();
                }
            } else {
                // create the video element without src so it doesn't start loading
                // until we want it to, otherwise it only starts loading when it's
                // added to the dom
                const videoElement = document.createElement("video");
                videoElement.muted = true;
                videoElement.preload = "auto";
                videoElement.src = preload ? data.src : "";

                // prevent video from tainting canvas
                videoElement.setAttribute("crossorigin", "anonymous");
                videoElement.setAttribute("playsinline", "");
                videoElement.addEventListener("error", e => {
                    const targetVideo = e.target as HTMLVideoElement;
                    switch (targetVideo.error?.code) {
                        case MediaError.MEDIA_ERR_DECODE:
                            captureError(
                                new Error(
                                    `MediaErrorDecode: ${targetVideo.error?.message}`,
                                ),
                                {
                                    message: `Potential hardware decode bug (${this.name}): ${targetVideo.src}: ${targetVideo.error?.message}`,
                                },
                            );
                            break;
                        default:
                    }
                });
                data.videoElement = videoElement;
            }
        }
        return data.videoElement;
    }

    preloadVideo(data: VideoData) {
        const videoElement = this.getVideo(data);
        if (!videoElement.src) {
            videoElement.src = data.src;
            videoElement.load();
        }
    }

    startRenderLoop() {
        if (this.aborted) {
            return;
        }
        // start render loop
        this.renderTimeoutHandle = window.requestAnimationFrame(() => {
            this.checkVideo();
            this.renderCanvas();
        });
    }

    stopRenderLoop() {
        if (this.renderTimeoutHandle) {
            window.cancelAnimationFrame(this.renderTimeoutHandle);
        }
    }

    checkVideo() {
        // lets check to see if we should be playing the next video
        if (this.shouldPlayVideos) {
            const currentVideoData = this.findCurrentVideoData();
            if (
                currentVideoData &&
                this.getVideo(currentVideoData, true) !== this.activeVideo
            ) {
                this.playCurrentVideo();
            }
            const nextVideoData = this.findNextVideoDataToPlay();
            if (nextVideoData) {
                const t = this.getEstimatedVideoTime();
                // if next video will start playing within the next second
                if (
                    !this.startedPreloading &&
                    t &&
                    nextVideoData.time - t < duration.second
                ) {
                    this.startedPreloading = true;
                    this.playVideo(nextVideoData);
                }
            }
        }
    }

    renderCanvas() {
        const { time, mode } = observeStore;
        const currentTimelineTime = time.getTime();
        if (this.activeVideo && this.ctx) {
            // resize canvas to video size and draw video
            if (
                this.canvas &&
                this.activeVideo.videoWidth > 0 &&
                this.activeVideo.videoHeight > 0 &&
                (this.activeVideo.videoWidth !== this.canvas.width ||
                    this.activeVideo.videoHeight !== this.canvas.height)
            ) {
                this.canvas.width = this.activeVideo.videoWidth;
                this.canvas.height = this.activeVideo.videoHeight;
            }
            if (
                (currentTimelineTime !== this.lastTimelineTime ||
                    mode !== "pause" ||
                    this.isPlaying) &&
                (this.activeVideo.readyState ===
                    this.activeVideo.HAVE_CURRENT_DATA ||
                    this.activeVideo.readyState ===
                        this.activeVideo.HAVE_ENOUGH_DATA)
            ) {
                this.ctx.drawImage(this.activeVideo, 0, 0);
                this.lastTimelineTime = currentTimelineTime;
            }
        }
        this.throttledAnimate();
    }

    findNextVideoDataToPlay(): VideoData | undefined {
        const cur = this.findCurrentVideoData();
        if (cur) {
            const index = this.videoDataList.indexOf(cur);
            if (index !== -1) {
                return this.videoDataList[index + 1];
            }
        }
        return undefined;
    }

    connectedCallback() {
        this.classList.add(styles["video-stack"]);
        // when element added to dom
        this.buildCanvas();
        this.throttledAnimate();
        this.sync();
    }

    disconnectedCallback() {
        this.aborted = true;
        this.stopRenderLoop();
    }

    static get observedAttributes() {
        return ["videos", "is-playing", "time", "speed", "muted", "name"];
    }

    attributeChangedCallback(name: string, _: string, newValue: string) {
        if (name === "videos") {
            this.handleVideosAttribute(newValue);
        } else if (name === "time") {
            this.handleTimeAttribute(newValue);
        } else if (name === "is-playing") {
            this.handleIsPlayingAttribute(newValue);
        } else if (name === "muted") {
            this.handleMutedAttribute(newValue);
        } else if (name === "speed") {
            this.handleSpeedAttribute(newValue);
        } else if (name === "name") {
            this.name = newValue;
        }
    }

    handleTimeAttribute(timeValue: string) {
        if (timeValue !== "") {
            this.videoTime = parseFloat(timeValue);
            this.timeSinceLastTimeSet = Date.now();
            this.sync();
        }
    }

    getEstimatedVideoTime(): number | undefined {
        if (this.videoTime && this.shouldPlayVideos) {
            return this.videoTime + (Date.now() - this.timeSinceLastTimeSet);
        }
        return this.videoTime;
    }

    handleSpeedAttribute(speedValue: string) {
        if (speedValue !== "") {
            this.speed = parseFloat(speedValue);
            if (this.activeVideo) {
                this.activeVideo.playbackRate = this.speed;
            }
            this.sync();
        }
    }

    handleIsPlayingAttribute(isPlayingValue: string) {
        if (isPlayingValue !== "") {
            this.shouldPlayVideos = isPlayingValue === "true";
            this.sync();
        }
    }

    handleMutedAttribute(muted: string) {
        const isMuted = muted === "true";
        if (this.activeVideo) {
            this.activeVideo.muted = isMuted;
        }
        this.muted = isMuted;
    }

    findClosestTimeIndex(time: number[]) {
        const currentEstimatedTime = this.getEstimatedVideoTime();
        if (!currentEstimatedTime) {
            return -1;
        }
        let closestIndex = 0;
        let closestDiff = Math.abs(time[0] - currentEstimatedTime);
        for (let i = 1; i < time.length; i++) {
            const diff = Math.abs(time[i] - currentEstimatedTime);
            if (diff < closestDiff) {
                closestIndex = i;
                closestDiff = diff;
            }
        }
        return closestIndex;
    }

    handleVideosAttribute(newValue: string) {
        if (newValue.trim().length === 0) {
            return;
        }

        // debounce video changes when scrubbing timeline
        const timeoutTime = this.shouldPlayVideos ? 0 : 300;
        clearTimeout(this.videoChangeTimeout);
        this.videoChangeTimeout = setTimeout(() => {
            const newVideoDataList = newValue
                .split(";")
                .map(x => x.split(","))
                .map(x => {
                    const src = x[0];
                    const existing = this.videoDataList.find(v => {
                        return (
                            this.getVideo(v, this.shouldPlayVideos).src === src
                        );
                    });
                    let el;
                    if (existing?.videoElement) {
                        el = existing.videoElement;
                    }
                    return {
                        videoElement: el,
                        src,
                        duration: parseFloat(x[1]),
                        time: parseFloat(x[2]),
                    } as VideoData;
                });
            // cleanup old videos
            this.videoDataList.forEach(d => {
                const found = newVideoDataList.find(
                    x => x.src === d.src && x.time === d.time,
                );
                if (!found) {
                    if (d.videoElement) {
                        // stop and remove from memory
                        d.videoElement.pause();
                        d.videoElement.src = "";
                        d.videoElement.load();
                        d.videoElement = undefined;
                    }
                    d.src = "";
                    d.time = 0;
                }
            });
            this.videoDataList = newVideoDataList;

            this.buildCanvas();
            this.sync();
        }, timeoutTime);
    }

    findCurrentVideoData(): VideoData | undefined {
        const currentEstimatedTime = this.getEstimatedVideoTime();
        if (currentEstimatedTime) {
            for (const [i, videoData] of this.videoDataList.entries()) {
                const timeDiff = currentEstimatedTime - videoData.time;
                let durationTilNextVideo = videoData.duration;
                if (i === this.videoDataList.length - 1 && timeDiff > 0) {
                    return videoData;
                }
                const next = this.videoDataList[i + 1];
                if (next) {
                    durationTilNextVideo = next.time - videoData.time;
                }
                if (timeDiff >= 0 && timeDiff < durationTilNextVideo) {
                    return videoData;
                }
            }
        }
        return undefined;
    }

    futureVideosExist(): boolean {
        const currentVideoData = this.findCurrentVideoData();
        if (currentVideoData) {
            return this.videoDataList.some(
                x =>
                    x.time >= currentVideoData.time + currentVideoData.duration,
            );
        }
        return false;
    }

    cleanupUnusedVideos() {
        const currentEstimatedTime = this.getEstimatedVideoTime();
        if (currentEstimatedTime !== undefined) {
            const timeLimit = duration.second * 10;
            for (const d of this.videoDataList) {
                if (
                    Math.abs(d.time + d.duration - currentEstimatedTime) >
                    timeLimit
                ) {
                    if (d.videoElement) {
                        // stop and remove from memory
                        d.videoElement.pause();
                        d.videoElement.src = "";
                        d.videoElement.load();
                        d.videoElement = undefined;
                    }
                    d.src = "";
                    d.time = 0;
                }
            }
        }
    }

    sync() {
        // should we show nothing
        if (this.videoDataList.length === 0) {
            if (this.canvas) {
                this.canvas.width = 0;
                this.canvas.height = 0;
            }
            return;
        }
        if (this.shouldPlayVideos && !this.isPlaying) {
            this.playCurrentVideo();
        } else if (!this.shouldPlayVideos) {
            if (this.isPlaying) {
                this.stopPlayingVideos();
            } else {
                this.showVideoPreview();
            }
        }
        this.cleanupUnusedVideos();
    }

    stopPlayingVideos() {
        if (this.activeVideo) {
            if (this.isVideoPlaying(this.activeVideo)) {
                this.pauseVideo(this.activeVideo);
            }
        }
        this.isPlaying = false;
    }

    showVideoPreview() {
        const currentEstimatedTime = this.getEstimatedVideoTime();
        if (currentEstimatedTime === undefined) {
            return;
        }

        if (this.activeVideo && this.isVideoPlaying(this.activeVideo)) {
            this.pauseVideo(this.activeVideo);
        }

        const videoData = this.findCurrentVideoData();
        if (videoData) {
            this.activeVideo = this.getVideo(videoData, true);

            const offset = currentEstimatedTime - videoData.time;
            this.getVideo(videoData).currentTime = offset / 1000;
        }
    }

    isVideoPlaying(video: HTMLVideoElement): boolean {
        // if its not playing, play, I know this is weird.
        // https://stackoverflow.com/questions/6877403/how-to-tell-if-a-video-element-is-currently-playing
        return !(
            video.paused ||
            video.ended ||
            video.seeking ||
            video.readyState < video.HAVE_FUTURE_DATA
        );
    }

    pauseVideo(video: HTMLVideoElement) {
        if (this.isVideoPlaying(video)) {
            this.videoTime = this.getEstimatedVideoTime();
            video.pause();
        }
    }

    playVideo(video: VideoData) {
        if (!video.videoElement) {
            return;
        }
        this.timeSinceLastTimeSet = Date.now();
        const result = video.videoElement.play();
        if (!result) {
            // sometimes this is undefined
            return;
        }
        result.catch(error => {
            // it's fine for play to be interrupted and receive weird formats
            // let's just not ruin the experience
            narrowError(
                error,
                DOMException,
                _ =>
                    _.code === DOMException.ABORT_ERR ||
                    _.code === DOMException.NOT_SUPPORTED_ERR,
            );
            // logger.debug("DOMException playing video", { error });
        });
    }

    updateSpeed(video: VideoData) {
        if (video.videoElement) {
            const playerSpeed = this.speed || 1;
            let baseSpeed = 1;
            const currentTime = this.getEstimatedVideoTime();
            const next = this.findNextVideoDataToPlay();
            // if our video is longer than telemetry time spans, lets speed up video
            if (currentTime && Number.isFinite(video.videoElement.duration)) {
                if (next) {
                    const timeDiff = next.time - video.time;
                    if (timeDiff !== 0) {
                        const timeLeftInVideo =
                            video.videoElement.duration * 1000;
                        const timeLeftBeforeNextDataPoint = timeDiff;
                        baseSpeed =
                            timeLeftInVideo / timeLeftBeforeNextDataPoint;
                    }
                }
            }
            video.videoElement.playbackRate = Math.max(
                Math.min(playerSpeed * baseSpeed, 16),
                0.0625,
            );
        }
    }

    playCurrentVideo() {
        this.startedPreloading = false;
        const currentEstimatedTime = this.getEstimatedVideoTime();
        if (currentEstimatedTime === undefined) {
            return;
        }

        const videoData = this.findCurrentVideoData();
        if (videoData) {
            let videoElementTimeOffset = currentEstimatedTime - videoData.time;
            const videoElement = this.getVideo(videoData, true);
            this.activeVideo = videoElement;
            if (!this.isVideoPlaying(videoElement)) {
                // if we are starting to play again, we shouldn't have an estimated time anymore
                this.timeSinceLastTimeSet = Date.now();
                videoElementTimeOffset =
                    defined(this.videoTime) - videoData.time;
                videoElement.currentTime = videoElementTimeOffset / 1000;
                this.playVideo(videoData);
            } else {
                const next = this.findNextVideoDataToPlay();
                if (next && Number.isFinite(videoElement.duration)) {
                    const timeDiff = next.time - videoData.time;
                    if (timeDiff !== 0) {
                        // if our video duration doesn't match our telemetry time diff, lets
                        // try to be more accurate where to jump to in our video
                        const ratio = videoElementTimeOffset / timeDiff;
                        videoElementTimeOffset =
                            videoElement.duration * 1000 * ratio;
                    }
                    this.preloadVideo(next);
                }
                videoElement.currentTime = videoElementTimeOffset / 1000;
            }
            this.videoDataList.forEach(d => {
                if (d.videoElement) {
                    d.videoElement.muted = true;
                }
            });
            videoElement.muted = this.muted;
            this.updateSpeed(videoData);
            this.isPlaying = true;
        } else if (this.canvas && !this.futureVideosExist()) {
            this.activeVideo = undefined;
            this.canvas.width = 0;
            this.canvas.height = 0;
        }
    }
}
window.customElements.define("video-stack", VideoStack);
